import React from 'react';
import { Settings } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Label } from '../../../components/ui/label';

const PREFERENCE_GROUPS = [
  {
    title: 'Orders & Delivery',
    items: [
      { key: 'delivery_cutoff_notifications', label: 'Order Deadline Reminders', desc: 'Get notified when an order cutoff deadline is approaching', defaultVal: false },
      { key: 'email_order_confirmations', label: 'Order Confirmations', desc: 'When your order is confirmed by admin', defaultVal: true },
      { key: 'email_delivery_notifications', label: 'Delivery Status Updates', desc: 'Production, out for delivery, and delivered alerts', defaultVal: true },
      { key: 'email_recurring_orders', label: 'Recurring Orders', desc: 'Review reminders, auto-placed order confirmations, and skipped-date alerts for your recurring schedules', defaultVal: true },
    ],
  },
  {
    title: 'Invoices & Payments',
    items: [
      { key: 'email_invoice_reminders', label: 'Invoice & Payment Alerts', desc: 'Invoice ready notifications and payment confirmations', defaultVal: true },
    ],
  },
  {
    title: 'Messages & Documents',
    items: [
      { key: 'email_admin_messages', label: 'Admin Messages', desc: 'When the admin sends you a message', defaultVal: true },
      { key: 'email_document_expiration', label: 'Document Expiration Alerts', desc: 'Notified when your T&C or Credit Agreement is expiring soon', defaultVal: true },
    ],
  },
];

export const PreferencesTab = ({ account, onUpdatePreference, canEdit = true }) => (
  <Card data-testid="preferences-card">
    <CardHeader>
      <CardTitle className="text-lg flex items-center gap-2">
        <Settings className="h-5 w-5 text-amber-500" />
        Order Preferences
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="p-4 bg-gray-50 rounded-lg">
          <Label className="text-gray-500">Minimum Order Amount</Label>
          <p className="font-medium text-lg mt-1">
            ${account.preferences?.minimum_order_amount?.toFixed(2) ?? '50.00'}
          </p>
        </div>
        <div className="p-4 bg-gray-50 rounded-lg">
          <Label className="text-gray-500">Payment Terms</Label>
          <p className="font-medium text-lg mt-1">
            {account.preferences?.payment_terms || 'Net 30'}
          </p>
        </div>
        <div className="p-4 bg-gray-50 rounded-lg md:col-span-2">
          <Label className="text-gray-500">Allowed Delivery Days</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {account.preferences?.allowed_delivery_days?.length > 0 ? (
              account.preferences.allowed_delivery_days.map(day => (
                <span key={day} className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium capitalize" data-testid={`profile-delivery-day-${day}`}>
                  {day}
                </span>
              ))
            ) : (
              <p className="text-sm text-gray-400">No delivery days configured - contact admin</p>
            )}
          </div>
        </div>
        <div className="p-4 bg-gray-50 rounded-lg">
          <Label className="text-gray-500">Preferred Time Window</Label>
          <p className="font-medium text-lg mt-1">
            {account.preferences?.preferred_delivery_time || '7:00 AM - 9:00 AM'}
          </p>
        </div>
      </div>

      <div className="pt-4 border-t">
        <h3 className="font-medium text-gray-900 mb-4">Notification Preferences</h3>
        <p className="text-xs text-gray-400 mb-4">Control which email alerts you receive.</p>
        {!canEdit && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="prefs-readonly-notice">
            <p className="text-sm text-amber-800">
              You have view-only access to preferences. Contact your account owner to make changes.
            </p>
          </div>
        )}
        <div className="space-y-5">
          {PREFERENCE_GROUPS.map(({ title, items }) => (
            <div key={title}>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{title}</p>
              <div className="space-y-2.5">
                {items.map(({ key, label, desc, defaultVal }) => (
                  <label key={key} className={`flex items-start gap-3 p-2 rounded-lg transition-colors ${canEdit ? 'cursor-pointer hover:bg-gray-50' : 'opacity-60 cursor-not-allowed'}`} data-testid={`pref-${key}`}>
                    <input
                      type="checkbox"
                      checked={account.preferences?.[key] ?? defaultVal}
                      onChange={(e) => onUpdatePreference(key, e.target.checked)}
                      disabled={!canEdit}
                      className="rounded border-gray-300 text-amber-500 focus:ring-amber-500 mt-0.5"
                    />
                    <div>
                      <span className="text-sm text-gray-700 font-medium">{label}</span>
                      <p className="text-xs text-gray-400">{desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </CardContent>
  </Card>
);
